import React, { useState, useEffect, useCallback } from 'react';
import { 
  User, ChefHat, Bike, MapPin, 
  Clock, CheckCircle, Phone, Utensils, 
  Settings, Plus, Trash2, Edit, Lock, LogOut,
  LayoutDashboard, Users, FileText, Image as ImageIcon, ChevronRight, Upload,
  Gift, Megaphone, Star, ChevronLeft, QrCode, History, Search, ArrowLeft, ArrowRight, AlertCircle, X, ChevronUp, ChevronDown, Calendar
} from 'lucide-react';

const API_BASE_URL = 'https://tid-udon.com/api.php'; 

// --- INITIAL STATE ---
const INITIAL_STORE_INFO = {
  name: "ลาบทิศอุดร",
  description: "ร้านอาหารอีสานรสเด็ด",
  logo: "https://via.placeholder.com/150",
  address: "99/9 อุดรธานี",
  phone: "081-999-9999",
  location: { lat: 13.7563, lng: 100.5018 },
  paymentQr: null, 
  loginBgGradient: "from-rose-100 to-red-200" 
};

// --- INITIAL DATA FALLBACKS ---
const INITIAL_BANNERS = [
  { id: 1, image_url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1080&h=720&q=80' },
  { id: 2, image_url: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=1080&h=720&q=80' }
];

const INITIAL_MENU = [
  { id: 1, name: 'ลาบหมูคั่ว', price: 80, details: 'สูตรดั้งเดิม', category: 'อาหารหลัก', isBestSeller: true, isAvailable: true, image: 'https://images.unsplash.com/photo-1595252643590-7d12f114c004?auto=format&fit=crop&w=300&q=80', promotionId: 1 },
];

const INITIAL_PROMOTIONS = [
  { id: 1, name: 'ส่วนลด 10%', type: 'percent', value: 10, pointsRequired: 100, startDate: '2024-01-01', endDate: '2025-12-31' },
];

const INITIAL_USERS = [];
const INITIAL_CUSTOMERS = [];

// --- UTILS ---
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371; 
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return parseFloat((R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2));
};
const formatCurrency = (a) => `฿${Number(a).toLocaleString()}`;
const isValidPhone = (p) => /^[0-9]{10}$/.test(p);

// --- COMPONENTS ---
const BannerSlider = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Safe check for banners array
  const hasBanners = banners && banners.length > 0;

  const nextSlide = useCallback(() => { 
    if (hasBanners) setCurrentIndex(prev => (prev + 1) % banners.length); 
  }, [hasBanners, banners]);

  useEffect(() => { 
    if (!hasBanners) return;
    const i = setInterval(nextSlide, 5000); 
    return () => clearInterval(i); 
  }, [nextSlide, hasBanners]);
  
  if (!hasBanners) return <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400">พื้นที่ประกาศ</div>;

  const currentBanner = banners[currentIndex] || banners[0];
  if (!currentBanner) return null;

  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-md bg-white border-4 border-white mb-6 group">
      <img 
        src={currentBanner.image_url || currentBanner.image} 
        alt="Banner" 
        className="w-full h-full object-cover transition-all"
        onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=No+Image'; }}
      />
      <button onClick={() => setCurrentIndex(prev => (prev - 1 + banners.length) % banners.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"><ChevronLeft size={24}/></button>
      <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"><ChevronRight size={24}/></button>
    </div>
  );
};

const MockMapSelector = ({ onLocationSelect, selectedLocation, storeLocation, readOnly = false }) => (
  <div className={`bg-rose-50 border-2 border-dashed border-rose-300 rounded-lg p-4 text-center relative h-56 flex flex-col items-center justify-center overflow-hidden ${!readOnly && 'group'}`}>
    <div className="absolute inset-0 opacity-20 bg-gray-300"></div>
    {selectedLocation ? (
      <div className="z-10 bg-white p-3 rounded shadow-lg border-l-4 border-rose-500">
        <p className="font-bold text-rose-700 flex items-center gap-2"><MapPin size={16} /> ปักหมุดแล้ว</p>
        {storeLocation && <p className="text-sm text-gray-600">{calculateDistance(storeLocation.lat, storeLocation.lng, selectedLocation.lat, selectedLocation.lng)} km</p>}
      </div>
    ) : (
      <div className="z-10">
        {!readOnly && <button onClick={() => onLocationSelect({ lat: 13.7850, lng: 100.5200 })} className="mt-2 bg-rose-500 text-white px-4 py-2 rounded-full shadow-md">ปักหมุดตำแหน่ง</button>}
      </div>
    )}
  </div>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center bg-rose-50 sticky top-0 z-10">
          <h3 className="font-bold text-lg text-rose-900">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-rose-200 rounded-full text-rose-500"><X size={20}/></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const Timer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((new Date() - new Date(startTime)) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
  const s = (elapsed % 60).toString().padStart(2, '0');
  return <span className="font-mono text-xl font-bold text-orange-600">{m}:{s}</span>;
};

export default function App() {
  const [currentUser, setCurrentUser] = useState(null); 
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [banners, setBanners] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  const [cart, setCart] = useState([]);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [storeInfo, setStoreInfo] = useState(INITIAL_STORE_INFO);
  
  const [activeTab, setActiveTab] = useState('menu'); 
  const [ownerActiveTab, setOwnerActiveTab] = useState('dashboard'); 
  const [userManageTab, setUserManageTab] = useState('staff'); 
  const [chefActiveTab, setChefActiveTab] = useState('active'); 
  const [riderActiveTab, setRiderActiveTab] = useState('active'); 
  const [riderStatsTab, setRiderStatsTab] = useState('day'); 

  const [loginForm, setLoginForm] = useState({ username: '', password: '', phone: '', name: '', address: '' });
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);
  const [menuForm, setMenuForm] = useState({});
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [promoForm, setPromoForm] = useState({});
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userForm, setUserForm] = useState({});
  
  const [slipModalImage, setSlipModalImage] = useState(null);
  const [slipImageFile, setSlipImageFile] = useState(null);
  const [orderNote, setOrderNote] = useState('');
  
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  
  const [deliveryType, setDeliveryType] = useState('delivery'); 
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({});

  // eslint-disable-next-line
  const [deliveryConfig] = useState({ baseDistance: 3, basePrice: 20, pricePerKm: 10 });
  const [pointConfig] = useState({ bahtPerPoint: 25 });

  useEffect(() => {
    const u = localStorage.getItem('tid_udon_user');
    if (u) setCurrentUser(JSON.parse(u));
  }, []);

  const fetchData = useCallback(async () => {
    try {
        const resMenu = await fetch(`${API_BASE_URL}?action=get_menu`);
        if(resMenu.ok) setMenuItems(await resMenu.json());

        if(currentUser) {
            const resOrders = await fetch(`${API_BASE_URL}?action=get_orders`);
            if(resOrders.ok) setOrders(await resOrders.json());
        }

        const resPromos = await fetch(`${API_BASE_URL}?action=get_promotions`);
        if(resPromos.ok) setPromotions(await resPromos.json());

        const resBanners = await fetch(`${API_BASE_URL}?action=get_banners`);
        if(resBanners.ok) setBanners(await resBanners.json());

        const resSettings = await fetch(`${API_BASE_URL}?action=get_settings`);
        if(resSettings.ok) {
            const data = await resSettings.json();
            if(!data.error) setStoreInfo(prev => ({...prev, ...data}));
        }

        if (currentUser && currentUser.role === 'owner') {
            const resUsers = await fetch(`${API_BASE_URL}?action=get_users`);
            if(resUsers.ok) {
               const d = await resUsers.json();
               if(Array.isArray(d)) {
                  setUsers(d.filter(u => u.role !== 'customer'));
                  setCustomers(d.filter(u => u.role === 'customer'));
               }
            }
        }
    } catch (e) { console.error(e); }
  }, [currentUser]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 10000); return () => clearInterval(i); }, [fetchData]);

  const handleLogin = async (e, type) => {
    e.preventDefault();
    if(type === 'customer' && isRegistering) {
       if(!isValidPhone(loginForm.phone)) return alert("เบอร์โทรไม่ถูกต้อง (ต้อง 10 หลัก)");
    }
    setLoading(true);
    try {
        let payload = {};
        let endpoint = 'login';
        if (type === 'customer') {
            if (isRegistering) {
                payload = { name: loginForm.name, phone: loginForm.phone, address: loginForm.address };
                endpoint = 'register';
            } else payload = { phone: loginForm.phone };
        } else payload = { username: loginForm.username, password: loginForm.password };

        const res = await fetch(`${API_BASE_URL}?action=${endpoint}`, { method: 'POST', body: JSON.stringify(payload) });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setCurrentUser(data);
        localStorage.setItem('tid_udon_user', JSON.stringify(data));
        if(isRegistering) alert("สมัครสมาชิกสำเร็จ!");
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('tid_udon_user');
    setCurrentUser(null);
    setCart([]);
  }

  const handleFileUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
        const res = await fetch(`${API_BASE_URL}?action=upload`, { method: 'POST', body: formData });
        const data = await res.json();
        if (data.url) callback(data.url); else alert('Upload failed');
    } catch (err) { alert('Upload error'); }
  };

  // --- CRUD WRAPPERS ---
  const postData = async (action, body, successMsg) => {
      try {
          const res = await fetch(`${API_BASE_URL}?action=${action}`, { method: 'POST', body: JSON.stringify(body) });
          const data = await res.json();
          if (data.success || data.user_id) {
              if(successMsg) alert(successMsg);
              fetchData();
              return true;
          } else { alert("Error: " + (data.error || "Unknown")); return false; }
      } catch (e) { alert("Connection Error"); return false; }
  };

  const handleSaveMenu = async () => {
      if (!menuForm.name || !menuForm.price) return alert("กรุณากรอกข้อมูล");
      if (await postData('save_menu', {...menuForm, price: Number(menuForm.price)}, "บันทึกเมนูสำเร็จ")) setIsMenuModalOpen(false);
  };

  const handleDeleteMenu = async (id) => {
      if(!window.confirm("ยืนยันการลบเมนูนี้?")) return;
      await postData('delete_menu', { id });
  };

  const handleSaveUser = async () => {
      if (userForm.role === 'customer' && (!userForm.name || !userForm.phone)) return alert("กรุณากรอกข้อมูล");
      if (await postData('save_user', userForm, "บันทึกผู้ใช้สำเร็จ")) setIsUserModalOpen(false);
  };

  const handleDeleteUser = async (id) => {
      if(!window.confirm("ยืนยันการลบผู้ใช้นี้?")) return;
      await postData('delete_user', { id });
  };

  const handleSavePromo = async () => {
      if (!promoForm.name) return alert("กรุณากรอกชื่อโปรโมชั่น");
      if (await postData('save_promo', promoForm, "บันทึกโปรโมชั่นสำเร็จ")) setIsPromoModalOpen(false);
  };

  const handleDeletePromo = async (id) => {
      if(!window.confirm("ลบโปรโมชั่นนี้?")) return;
      await postData('delete_promo', { id });
  };

  const handleAddBanner = async (url) => {
      await postData('save_banner', { image_url: url }, "เพิ่มประกาศสำเร็จ");
  };

  const handleDeleteBanner = async (id) => {
      if(!window.confirm("ลบประกาศนี้?")) return;
      await postData('delete_banner', { id });
  };

  const handleSaveSettings = async () => {
      await postData('save_settings', storeInfo, "บันทึกการตั้งค่าเรียบร้อย");
  };

  const reorderBanners = async (direction, index) => {
      const newBanners = [...banners];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex >= 0 && targetIndex < newBanners.length) {
          [newBanners[index], newBanners[targetIndex]] = [newBanners[targetIndex], newBanners[index]];
          const payload = newBanners.map((b, i) => ({ id: b.id, display_order: i }));
          setBanners(newBanners); 
          await postData('reorder_banners', { banners: payload });
      }
  };

  // Fix: Customer Add to Cart Logic (Merge duplicates)
  const addToCart = (item) => {
      setCart(prev => {
          const existing = prev.find(i => (i.id === (item.menu_id || item.id)));
          if(existing) {
             return prev.map(i => (i.id === (item.menu_id || item.id)) ? { ...i, quantity: i.quantity + 1 } : i);
          } else {
             return [...prev, { id: item.menu_id || item.id, name: item.name, price: Number(item.price), quantity: 1, note: '' }];
          }
      });
  };

  const updateCartItem = (id, change, note) => {
    setCart(prev => prev.map(item => {
       if (item.id !== id) return item;
       if (note !== undefined) return { ...item, note };
       const newQty = item.quantity + change;
       return newQty > 0 ? { ...item, quantity: newQty } : item;
    }));
  };
  
  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.id !== id));

  const placeOrder = async () => {
     if (deliveryType === 'delivery' && !customerLocation) return alert("ปักหมุดที่อยู่ก่อน");
     if (!slipImageFile) return alert("อัปโหลดสลิปก่อน");

     const distance = deliveryType === 'delivery' ? calculateDistance(storeInfo.location.lat, storeInfo.location.lng, customerLocation.lat, customerLocation.lng) : 0;
     const deliveryFee = deliveryType === 'delivery' ? (distance <= deliveryConfig.baseDistance ? deliveryConfig.basePrice : deliveryConfig.basePrice + (distance - deliveryConfig.baseDistance) * deliveryConfig.pricePerKm) : 0;
     const foodTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
     let discount = 0;
     if (selectedPromo) discount = selectedPromo.type === 'percent' ? (foodTotal * selectedPromo.value / 100) : selectedPromo.value;
     const grandTotal = Math.max(0, foodTotal - discount) + deliveryFee;

     const orderData = {
         customerId: currentUser.user_id || currentUser.id,
         type: deliveryType,
         foodTotal,
         deliveryFee,
         total: grandTotal,
         distance,
         location: customerLocation || {lat:0, lng:0},
         items: cart,
         orderNote,
         slipImage: slipImageFile
     };

     if (await postData('place_order', orderData, "สั่งซื้อสำเร็จ! รอการยืนยัน")) {
         setCart([]); setSlipImageFile(null); setOrderNote(''); setActiveTab('history');
     }
  };

  const updateStatus = async (id, status, extraData = {}) => {
      try {
        await fetch(`${API_BASE_URL}?action=update_status`, { method: 'POST', body: JSON.stringify({ orderId: id, status }) });
        fetchData();
      } catch (e) { console.error(e); }
  };

  const cancelOrder = async (id, reason) => {
     try {
        await fetch(`${API_BASE_URL}?action=update_status`, { method: 'POST', body: JSON.stringify({ orderId: id, status: 'cancelled', reason }) });
        fetchData();
     } catch (e) { console.error(e); }
  };

  const submitReview = async () => {
     try {
        await fetch(`${API_BASE_URL}?action=submit_review`, { method: 'POST', body: JSON.stringify(reviewForm) });
        setIsReviewModalOpen(false);
        alert("ส่งรีวิวเรียบร้อย ขอบคุณครับ!");
        fetchData();
     } catch (e) { alert("ส่งรีวิวไม่ได้"); }
  };

  // --- STATS LOGIC ---
  const getPeriodStats = (data, period) => {
     const now = new Date();
     return data.filter(d => {
        const date = new Date(d.timestamp || d.created_at);
        if (period === 'day') return date.toDateString() === now.toDateString();
        if (period === 'week') {
           const oneWeekAgo = new Date(now.setDate(now.getDate() - 7));
           return date >= oneWeekAgo;
        }
        if (period === 'month') return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        return true;
     });
  };

  const getRiderStats = (rid, period) => {
      const myOrders = orders.filter(o => o.rider_id == rid && o.status === 'completed');
      const now = new Date();
      const filtered = myOrders.filter(o => {
          const d = new Date(o.created_at);
          if (period === 'day') return d.toDateString() === now.toDateString();
          if (period === 'week') return d >= new Date(now.setDate(now.getDate()-7));
          return d.getMonth() === now.getMonth();
      });
      const income = filtered.reduce((sum, o) => sum + Number(o.delivery_fee), 0);
      // Detailed history list
      return { count: filtered.length, income, orders: filtered };
  };

  const getCustomerStats = (customerId) => {
    const custOrders = orders.filter(o => o.customer_id == customerId);
    const completed = custOrders.filter(o => o.status === 'completed');
    const cancelled = custOrders.filter(o => o.status === 'cancelled');
    return { 
        totalOrders: custOrders.length, 
        completed: completed.length, 
        cancelled: cancelled.length
    };
  };

  // --- RENDER ---

  if (!currentUser) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${storeInfo.loginBgGradient} font-sans flex flex-col items-center py-10 px-4`}>
        <div className="bg-white/95 backdrop-blur-sm p-6 rounded-3xl shadow-2xl w-full max-w-md relative overflow-hidden mb-8 border border-white/50">
          <div className="flex justify-center mb-4"><img src={storeInfo.logo} className="w-24 h-24 rounded-full border-4 border-rose-200 shadow-lg object-cover" alt="logo"/></div>
          <div className="text-center mb-2"><h1 className="text-2xl font-extrabold text-rose-800">{storeInfo.name}</h1><p className="text-rose-500 font-bold flex items-center justify-center gap-1"><Phone size={14}/> {storeInfo.phone}</p></div>
          <div className="text-center text-sm text-gray-500 mb-6 bg-rose-50 p-2 rounded-lg"><MapPin size={12} className="inline mr-1"/> {storeInfo.address}</div>

          <form onSubmit={(e) => handleLogin(e, 'customer')} className="space-y-4 border-b pb-6 mb-6">
             {isRegistering ? (
                <div className="space-y-3 animate-fade-in">
                  <p className="text-center font-bold text-rose-600">สมัครสมาชิกใหม่</p>
                  <input type="text" placeholder="ชื่อ-นามสกุล" className="w-full bg-rose-50 border px-4 py-3 rounded-xl" onChange={e => setLoginForm({...loginForm, name: e.target.value})} required />
                  <input type="tel" placeholder="เบอร์โทรศัพท์ (10 หลัก)" className="w-full bg-rose-50 border px-4 py-3 rounded-xl" onChange={e => setLoginForm({...loginForm, phone: e.target.value})} required />
                  <textarea placeholder="ที่อยู่จัดส่ง (บ้านเลขที่/ซอย/ถนน)" className="w-full bg-rose-50 border px-4 py-3 rounded-xl h-24" onChange={e => setLoginForm({...loginForm, address: e.target.value})} required />
                </div>
             ) : (
                <input type="tel" placeholder="เบอร์โทรศัพท์ลูกค้า" className="w-full bg-rose-50 border px-4 py-3 rounded-xl text-lg text-center" onChange={e => setLoginForm({...loginForm, phone: e.target.value})} required />
             )}
             <button type="submit" disabled={loading} className="w-full bg-rose-500 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-rose-600 transition text-lg">{loading ? 'Loading...' : (isRegistering ? 'ยืนยันการสมัคร' : 'เข้าสู่ระบบลูกค้า')}</button>
             <div className="text-center"><button type="button" onClick={() => setIsRegistering(!isRegistering)} className="text-rose-500 font-bold underline text-sm">{isRegistering ? 'มีบัญชีอยู่แล้ว?' : 'สมัครสมาชิกใหม่'}</button></div>
          </form>

          <BannerSlider banners={banners.length > 0 ? banners : INITIAL_BANNERS} />

          <div className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-300 mt-6">
             <h3 className="text-center font-bold text-gray-500 text-xs mb-2 flex items-center justify-center gap-1"><Lock size={12}/> เข้าสู่ระบบพนักงาน</h3>
             <form onSubmit={(e) => handleLogin(e, 'staff')} className="flex gap-2">
                <input type="text" placeholder="User" className="flex-1 border p-1.5 rounded text-sm" onChange={e => setLoginForm({...loginForm, username: e.target.value})} />
                <input type="password" placeholder="Pass" className="flex-1 border p-1.5 rounded text-sm" onChange={e => setLoginForm({...loginForm, password: e.target.value})} />
                <button type="submit" className="bg-gray-700 text-white px-3 rounded text-sm font-bold">Login</button>
             </form>
          </div>
        </div>

        <div className="w-full max-w-md bg-white p-4 rounded-xl shadow mb-8">
           <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2"><MapPin size={16}/> แผนที่ร้าน</h3>
           <MockMapSelector selectedLocation={storeInfo.location} readOnly={true} />
        </div>
      </div>
    );
  }

  // --- OWNER VIEW ---
  if (currentUser.role === 'owner') {
    return (
      <div className="min-h-screen bg-rose-50 font-sans flex flex-col md:flex-row">
        <div className="bg-rose-900 w-full md:w-64 p-6 flex flex-col justify-between">
           <div>
             <div className="flex items-center gap-3 mb-8"><img src={storeInfo.logo} className="w-12 h-12 rounded-full bg-white p-1 object-cover" alt="logo"/><div><h1 className="font-bold text-white">{storeInfo.name}</h1><p className="text-xs text-rose-200">Owner</p></div></div>
             <nav className="space-y-1">
               {[{id:'dashboard',l:'สรุปยอดขาย'},{id:'orders',l:'จัดการออร์เดอร์'},{id:'menu',l:'เมนูอาหาร'},{id:'promotions',l:'โปรโมชัน'},{id:'users',l:'ผู้ใช้งาน'},{id:'banners',l:'ประกาศ'},{id:'settings',l:'ตั้งค่า'}].map(i => (
                 <button key={i.id} onClick={() => setOwnerActiveTab(i.id)} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium ${ownerActiveTab===i.id?'bg-rose-100 text-rose-900':'text-rose-100 hover:bg-rose-800'}`}>{i.l}</button>
               ))}
             </nav>
           </div>
           <button onClick={handleLogout} className="text-rose-200 hover:text-white flex items-center gap-2"><LogOut size={16}/> ออกจากระบบ</button>
        </div>
        
        <div className="flex-1 p-6 overflow-y-auto h-screen">
           {/* DASHBOARD */}
           {ownerActiveTab === 'dashboard' && (
             <div className="space-y-6">
               <h2 className="text-2xl font-bold text-rose-900">สรุปยอดขายภาพรวม</h2>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                 <div className="bg-white p-6 rounded-xl shadow border-l-4 border-blue-500">
                   <p className="text-gray-500">ออร์เดอร์วันนี้</p>
                   <p className="text-3xl font-bold">{getPeriodStats(orders, 'day').length}</p>
                 </div>
                 <div className="bg-white p-6 rounded-xl shadow border-l-4 border-orange-500">
                   <p className="text-gray-500">รอปิดการขาย</p>
                   <p className="text-3xl font-bold text-orange-600">{orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length}</p>
                 </div>
                 <div className="bg-white p-6 rounded-xl shadow border-l-4 border-green-500">
                   <p className="text-gray-500">ปิดการขายแล้ว</p>
                   <p className="text-3xl font-bold text-green-600">{orders.filter(o => o.status === 'completed').length}</p>
                 </div>
               </div>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-6 rounded-xl shadow border-l-4 border-rose-500">
                     <h3 className="font-bold text-gray-700 mb-2">ยอดขายอาหาร (เฉพาะ Completed)</h3>
                     <div className="flex justify-between border-b py-1"><span>วันนี้</span><span className="font-bold text-rose-600">{formatCurrency(getPeriodStats(orders.filter(o=>o.status==='completed'), 'day').reduce((a,b)=>a+Number(b.food_total || b.foodTotal),0))}</span></div>
                     <div className="flex justify-between border-b py-1"><span>สัปดาห์นี้</span><span className="font-bold text-rose-600">{formatCurrency(getPeriodStats(orders.filter(o=>o.status==='completed'), 'week').reduce((a,b)=>a+Number(b.food_total || b.foodTotal),0))}</span></div>
                     <div className="flex justify-between py-1"><span>เดือนนี้</span><span className="font-bold text-rose-600">{formatCurrency(getPeriodStats(orders.filter(o=>o.status==='completed'), 'month').reduce((a,b)=>a+Number(b.food_total || b.foodTotal),0))}</span></div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow border-l-4 border-purple-500">
                     <h3 className="font-bold text-gray-700 mb-2">รายได้ค่าส่ง (แยกต่างหาก)</h3>
                     <div className="flex justify-between border-b py-1"><span>วันนี้</span><span className="font-bold text-purple-600">{formatCurrency(getPeriodStats(orders.filter(o=>o.status==='completed'), 'day').reduce((a,b)=>a+Number(b.delivery_fee || b.deliveryFee),0))}</span></div>
                     <div className="flex justify-between border-b py-1"><span>สัปดาห์นี้</span><span className="font-bold text-purple-600">{formatCurrency(getPeriodStats(orders.filter(o=>o.status==='completed'), 'week').reduce((a,b)=>a+Number(b.delivery_fee || b.deliveryFee),0))}</span></div>
                     <div className="flex justify-between py-1"><span>เดือนนี้</span><span className="font-bold text-purple-600">{formatCurrency(getPeriodStats(orders.filter(o=>o.status==='completed'), 'month').reduce((a,b)=>a+Number(b.delivery_fee || b.deliveryFee),0))}</span></div>
                  </div>
               </div>
             </div>
           )}

           {/* ORDERS */}
           {ownerActiveTab === 'orders' && (
             <div className="space-y-8">
               <h2 className="text-2xl font-bold text-rose-900">จัดการออร์เดอร์</h2>
               
               <div>
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-red-600 bg-red-50 p-2 rounded-lg"><Clock/> รอตรวจสอบการชำระเงิน</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {orders.filter(o => o.status === 'waiting_payment').map(order => (
                      <div key={order.order_id || order.id} className="bg-white border-2 border-red-500 rounded-xl p-4 shadow-lg relative">
                         <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 absolute top-0 right-0 rounded-bl-lg">รอโอน</div>
                         <p className="font-bold">{order.order_id || order.id}</p>
                         <p className="text-sm">ลูกค้า: {order.customerName}</p>
                         <p className="text-sm font-bold text-red-600">ยอด: {formatCurrency(order.grand_total || order.total)}</p>
                         <div className="my-2 h-32 bg-gray-100 rounded flex items-center justify-center cursor-pointer overflow-hidden" onClick={() => setSlipModalImage(order.slip_image_url || order.slipImage)}>
                            {order.slip_image_url || order.slipImage ? <img src={order.slip_image_url || order.slipImage} className="w-full h-full object-cover" alt="slip"/> : "ไม่มีสลิป"}
                         </div>
                         <div className="grid grid-cols-2 gap-2">
                           <button onClick={() => updateStatus(order.order_id || order.id, 'cooking')} className="bg-orange-500 text-white py-1 rounded text-sm hover:bg-orange-600">ยืนยัน (ปรุง)</button>
                           <button onClick={() => {
                              const reason = prompt("ระบุเหตุผลการยกเลิก (ให้ลูกค้าเห็น):");
                              if (reason) cancelOrder(order.order_id || order.id, reason);
                           }} className="bg-gray-200 text-gray-700 py-1 rounded text-sm hover:bg-gray-300">แจ้ง/ยกเลิก</button>
                         </div>
                      </div>
                    ))}
                    {orders.filter(o => o.status === 'waiting_payment').length === 0 && <p className="text-gray-400 text-sm col-span-3 text-center py-4 bg-white rounded-xl border border-dashed">ไม่มีรายการรอตรวจสอบ</p>}
                  </div>
               </div>

               <div>
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-orange-600 bg-orange-50 p-2 rounded-lg"><ChefHat/> กำลังเตรียมอาหาร</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {orders.filter(o => o.status === 'cooking').map(order => (
                       <div key={order.order_id || order.id} className="bg-white border-2 border-orange-400 rounded-xl p-4 shadow">
                          <p className="font-bold">{order.order_id || order.id}</p>
                          <p className="text-sm">{order.items ? order.items.length : 0} รายการ</p>
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded mt-2 inline-block">พ่อครัวกำลังทำ...</span>
                       </div>
                    ))}
                  </div>
               </div>

               <div>
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-yellow-600 bg-yellow-50 p-2 rounded-lg"><Bike/> รอส่ง / กำลังส่ง</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {orders.filter(o => ['ready_to_ship', 'delivering'].includes(o.status)).map(order => (
                       <div key={order.order_id || order.id} className="bg-white border-2 border-yellow-400 rounded-xl p-4 shadow">
                          <p className="font-bold">{order.order_id || order.id}</p>
                          <p className="text-sm">ลูกค้า: {order.customerName}</p>
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded mt-2 inline-block">
                             {order.status === 'ready_to_ship' ? 'รอไรเดอร์/รับเอง' : 'กำลังจัดส่ง'}
                          </span>
                       </div>
                    ))}
                  </div>
               </div>

               <div className="mt-8 pt-8 border-t">
                  <h3 className="font-bold text-lg mb-4 flex items-center justify-between">
                    <span className="flex items-center gap-2"><History/> ประวัติออร์เดอร์ (สำเร็จ/ยกเลิก)</span>
                    <div className="relative">
                      <Search size={14} className="absolute left-2 top-2.5 text-gray-400"/>
                      <input type="text" placeholder="ค้นหา Order ID" className="border rounded-full pl-8 pr-4 py-1 text-sm" value={orderSearchTerm} onChange={e => setOrderSearchTerm(e.target.value)} />
                    </div>
                  </h3>
                  <div className="bg-white rounded-xl shadow overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-500 font-bold">
                        <tr><th className="p-3">ID</th><th className="p-3">วันที่</th><th className="p-3">ลูกค้า</th><th className="p-3">ยอดเงิน</th><th className="p-3">สถานะ</th><th className="p-3">รีวิว</th></tr>
                      </thead>
                      <tbody className="divide-y">
                        {orders.filter(o => (o.status === 'completed' || o.status === 'cancelled') && (String(o.order_id || o.id).includes(orderSearchTerm)))
                           .slice((historyPage-1)*50, historyPage*50).map(order => (
                           <tr key={order.order_id || order.id} className="hover:bg-gray-50">
                              <td className="p-3 font-mono">{order.order_id || order.id}</td>
                              <td className="p-3">{new Date(order.timestamp || order.created_at).toLocaleString()}</td>
                              <td className="p-3">{order.customerName}</td>
                              <td className="p-3">{formatCurrency(order.grand_total || order.total)}</td>
                              <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${order.status==='completed'?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>{order.status}</span></td>
                              <td className="p-3">
                                 {order.review ? (
                                    <div className="text-xs">
                                       <span className="text-yellow-500 font-bold">{order.review.rating} ดาว</span> <br/>
                                       <span className="text-gray-500">"{order.review.comment}"</span>
                                    </div>
                                 ) : '-'}
                              </td>
                           </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="p-3 border-t flex justify-between items-center bg-gray-50">
                       <span className="text-xs text-gray-500">แสดงผล 50 รายการ/หน้า</span>
                       <div className="flex gap-2">
                          <button disabled={historyPage===1} onClick={()=>setHistoryPage(p=>p-1)} className="p-1 rounded bg-white border disabled:opacity-50"><ArrowLeft size={16}/></button>
                          <span className="px-2">{historyPage}</span>
                          <button onClick={()=>setHistoryPage(p=>p+1)} className="p-1 rounded bg-white border"><ArrowRight size={16}/></button>
                       </div>
                    </div>
                  </div>
               </div>
             </div>
           )}

           {/* MENU MANAGEMENT (Updated) */}
           {ownerActiveTab === 'menu' && (
             <div>
               <div className="flex justify-between items-center mb-4">
                 <h2 className="text-2xl font-bold text-rose-900">จัดการเมนูอาหาร</h2>
                 <button onClick={() => { setMenuForm({ id: null, name: '', price: '', details: '', image: '', category: 'อาหารหลัก', isBestSeller: false, isAvailable: true, promotionId: '' }); setIsMenuModalOpen(true); }} className="bg-rose-600 text-white px-4 py-2 rounded-xl font-bold flex gap-2"><Plus/> เพิ่มเมนู</button>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                 {menuItems.map(item => (
                   <div key={item.menu_id || item.id} className={`bg-white p-4 rounded-xl shadow-sm border flex gap-3 relative ${!item.is_available && !item.isAvailable && 'opacity-60 bg-gray-50'}`}>
                      {(!item.is_available && !item.isAvailable) && <span className="absolute inset-0 flex items-center justify-center font-bold text-red-600 text-xl border-4 border-red-500 rounded-xl bg-white/50 transform rotate-12 z-10">หมด / งดขาย</span>}
                      {(item.is_best_seller || item.isBestSeller) && <div className="absolute top-2 right-2 bg-yellow-400 text-xs font-bold px-2 py-0.5 rounded-full shadow z-20">ขายดี</div>}
                      <img src={item.image_url || item.image} className="w-24 h-24 object-cover rounded-lg bg-gray-200" alt={item.name}/>
                      <div className="flex-1">
                        <h4 className="font-bold">{item.name}</h4>
                        <p className="text-xs text-gray-500">{item.category}</p>
                        <p className="text-rose-600 font-bold">{formatCurrency(item.price)}</p>
                        {item.promotionId && <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded block mt-1">ร่วมโปรโมชัน</span>}
                        <div className="flex gap-2 mt-2 relative z-20">
                           <button onClick={() => { setMenuForm(item); setIsMenuModalOpen(true); }} className="text-blue-500 bg-blue-50 p-1.5 rounded"><Edit size={14}/></button>
                           <button onClick={() => handleDeleteMenu(item.menu_id || item.id)} className="text-red-500 bg-red-50 p-1.5 rounded"><Trash2 size={14}/></button>
                        </div>
                      </div>
                   </div>
                 ))}
               </div>
               <Modal isOpen={isMenuModalOpen} onClose={() => setIsMenuModalOpen(false)} title="จัดการเมนู">
                  <div className="space-y-3">
                     <div className="flex justify-center mb-2">
                        <label className="cursor-pointer bg-gray-100 w-32 h-32 flex items-center justify-center rounded-lg overflow-hidden border hover:border-rose-500">
                           {menuForm.image ? <img src={menuForm.image} className="w-full h-full object-cover" alt="preview"/> : <ImageIcon className="text-gray-400"/>}
                           <input type="file" className="hidden" onChange={e => handleFileUpload(e, (url) => setMenuForm({...menuForm, image: url}))}/>
                        </label>
                     </div>
                     <input type="text" placeholder="ชื่อเมนู" className="w-full border p-2 rounded" value={menuForm.name} onChange={e => setMenuForm({...menuForm, name: e.target.value})}/>
                     <div className="flex gap-2">
                       <input type="number" placeholder="ราคา" className="flex-1 border p-2 rounded" value={menuForm.price} onChange={e => setMenuForm({...menuForm, price: e.target.value})}/>
                       <select className="flex-1 border p-2 rounded" value={menuForm.category} onChange={e => setMenuForm({...menuForm, category: e.target.value})}>
                          <option value="อาหารหลัก">อาหารหลัก</option><option value="ข้าว">ข้าว</option><option value="ของกินเล่น">ของกินเล่น</option><option value="อื่นๆ">อื่นๆ</option>
                       </select>
                     </div>
                     <textarea placeholder="รายละเอียด" className="w-full border p-2 rounded" value={menuForm.details} onChange={e => setMenuForm({...menuForm, details: e.target.value})}/>
                     <select className="w-full border p-2 rounded" value={menuForm.promotionId || ''} onChange={e => setMenuForm({...menuForm, promotionId: e.target.value ? Number(e.target.value) : ''})}>
                        <option value="">-- ไม่ร่วมโปรโมชัน --</option>
                        {promotions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                     </select>
                     <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer bg-yellow-50 p-2 rounded border border-yellow-200 flex-1">
                           <input type="checkbox" checked={menuForm.isBestSeller} onChange={e => setMenuForm({...menuForm, isBestSeller: e.target.checked})}/>
                           <span className="text-sm font-bold text-yellow-700">เมนูขายดี</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-green-50 p-2 rounded border border-green-200 flex-1">
                           <input type="checkbox" checked={menuForm.isAvailable} onChange={e => setMenuForm({...menuForm, isAvailable: e.target.checked})}/>
                           <span className="text-sm font-bold text-green-700">มีจำหน่าย</span>
                        </label>
                     </div>
                     <button onClick={handleSaveMenu} className="w-full bg-rose-600 text-white py-2 rounded font-bold">บันทึก</button>
                  </div>
               </Modal>
             </div>
           )}

           {/* USER MANAGEMENT (With Points Edit) */}
           {ownerActiveTab === 'users' && (
             <div>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-4 border-b">
                     <button onClick={() => setUserManageTab('staff')} className={`pb-2 px-4 font-bold ${userManageTab==='staff'?'text-rose-600 border-b-2 border-rose-600':'text-gray-400'}`}>พนักงาน (ไรเดอร์/เชฟ)</button>
                     <button onClick={() => setUserManageTab('customers')} className={`pb-2 px-4 font-bold ${userManageTab==='customers'?'text-rose-600 border-b-2 border-rose-600':'text-gray-400'}`}>ลูกค้า</button>
                  </div>
                  <button onClick={() => { setUserForm({id:null, name:'', username:'', password:'', role:'customer', points:0}); setIsUserModalOpen(true); }} className="bg-rose-600 text-white px-3 py-1 rounded-lg text-sm font-bold">+ เพิ่มผู้ใช้</button>
                </div>
                
                {userManageTab === 'staff' && (
                   <div className="space-y-4">
                      {users.filter(u => u.role !== 'owner').map(u => {
                         const stats = u.role === 'rider' ? getRiderStats(u.user_id || u.id) : null;
                         return (
                            <div key={u.user_id || u.id} className="bg-white p-4 rounded-xl shadow-sm border">
                               <div className="flex justify-between items-start">
                                  <div>
                                     <p className="font-bold flex items-center gap-2">{u.full_name || u.name} <span className="text-xs bg-gray-100 px-2 py-0.5 rounded uppercase">{u.role}</span></p>
                                     <p className="text-sm text-gray-500">User: {u.username}</p>
                                  </div>
                                  <div className="flex gap-2">
                                     <button onClick={() => { setUserForm(u); setIsUserModalOpen(true); }} className="text-blue-500"><Edit size={16}/></button>
                                     <button onClick={() => handleDeleteUser(u.user_id || u.id)} className="text-red-500"><Trash2 size={16}/></button>
                                  </div>
                               </div>
                               {/* Rider History (Income separated by day) */}
                               {u.role === 'rider' && stats && (
                                  <div className="mt-4 bg-gray-50 p-3 rounded-lg text-sm">
                                     <p className="font-bold text-gray-700 mb-2">ประวัติการงาน (ไรเดอร์)</p>
                                     <div className="grid grid-cols-3 gap-2 mb-2 text-center bg-white p-2 rounded">
                                        <div><span className="block text-xs text-gray-400">ส่งสำเร็จ</span><b>{stats.count}</b></div>
                                        <div><span className="block text-xs text-gray-400">รายได้รวม</span><b className="text-green-600">{formatCurrency(stats.income)}</b></div>
                                     </div>
                                     <div className="max-h-32 overflow-y-auto space-y-1">
                                        {stats.history.map((day, idx) => (
                                           <div key={idx} className="flex justify-between text-xs border-b pb-1">
                                              <span>{new Date(day.timestamp || day.created_at).toLocaleDateString()}</span>
                                              <span>{formatCurrency(day.delivery_fee || day.deliveryFee)}</span>
                                           </div>
                                        ))}
                                     </div>
                                  </div>
                               )}
                            </div>
                         )
                      })}
                   </div>
                )}
                
                {userManageTab === 'customers' && (
                   <div className="space-y-4">
                      {customers.map(c => {
                         const stats = getCustomerStats(c.user_id || c.id);
                         return (
                            <div key={c.user_id || c.id} className="bg-white p-4 rounded-xl shadow-sm border">
                               <div className="flex justify-between">
                                  <div>
                                     <p className="font-bold">{c.full_name || c.name}</p>
                                     <p className="text-sm text-gray-500">{c.phone_number || c.phone}</p>
                                     <p className="text-xs text-gray-400">{c.address}</p>
                                  </div>
                                  <div className="text-right">
                                     <div className="flex gap-2 justify-end mb-2">
                                       <button onClick={() => { setUserForm({...c, role:'customer'}); setIsUserModalOpen(true); }} className="text-blue-500"><Edit size={16}/></button>
                                       <button onClick={() => handleDeleteUser(c.user_id || c.id)} className="text-red-500"><Trash2 size={16}/></button>
                                     </div>
                                     <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold"><Star size={10} className="inline"/> {c.points} แต้ม</span>
                                  </div>
                               </div>
                               <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs bg-gray-50 p-2 rounded">
                                  <div><span className="block text-gray-400">สั่งซื้อ</span>{stats.totalOrders}</div>
                                  <div><span className="block text-gray-400">สำเร็จ</span>{stats.completed}</div>
                                  <div><span className="block text-gray-400">ยกเลิก</span>{stats.cancelled}</div>
                               </div>
                            </div>
                         )
                      })}
                   </div>
                )}
                <Modal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} title="จัดการผู้ใช้">
                   <div className="space-y-3">
                      <select className="w-full border p-2 rounded" value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})}>
                         <option value="customer">ลูกค้า</option><option value="rider">ไรเดอร์</option><option value="chef">พ่อครัว</option>
                      </select>
                      <input type="text" placeholder="ชื่อ-นามสกุล" className="w-full border p-2 rounded" value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})}/>
                      {userForm.role === 'customer' ? (
                         <>
                            <input type="tel" placeholder="เบอร์โทร" className="w-full border p-2 rounded" value={userForm.phone} onChange={e => setUserForm({...userForm, phone: e.target.value})}/>
                            <textarea placeholder="ที่อยู่" className="w-full border p-2 rounded" value={userForm.address} onChange={e => setUserForm({...userForm, address: e.target.value})}/>
                            <label className="block text-xs font-bold mt-2">แต้มสะสม</label>
                            <input type="number" className="w-full border p-2 rounded" value={userForm.points} onChange={e => setUserForm({...userForm, points: Number(e.target.value)})}/>
                         </>
                      ) : (
                         <>
                            <input type="text" placeholder="Username" className="w-full border p-2 rounded" value={userForm.username} onChange={e => setUserForm({...userForm, username: e.target.value})}/>
                            <input type="text" placeholder="Password" className="w-full border p-2 rounded" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})}/>
                         </>
                      )}
                      <button onClick={handleSaveUser} className="w-full bg-rose-600 text-white py-2 rounded font-bold">บันทึก</button>
                   </div>
                </Modal>
             </div>
           )}

           {/* PROMOTIONS & BANNERS & SETTINGS ... (Same as before) */}
           {ownerActiveTab === 'promotions' && (
             <div>
               <div className="flex justify-between items-center mb-4">
                 <h2 className="text-2xl font-bold text-rose-900">จัดการโปรโมชัน</h2>
                 <button onClick={() => { setPromoForm({ id: null, name: '', type: 'percent', value: 0, pointsRequired: 0, startDate: '', endDate: '' }); setIsPromoModalOpen(true); }} className="bg-rose-600 text-white px-4 py-2 rounded-xl font-bold flex gap-2"><Plus/> สร้างโปรโมชัน</button>
               </div>
               <div className="grid gap-4">
                 {promotions.map(p => (
                   <div key={p.id} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-rose-400 flex justify-between">
                      <div>
                        <h4 className="font-bold text-lg">{p.name}</h4>
                        <p className="text-sm">ลด {p.value} {p.type === 'percent'?'%':'บาท'} (ใช้ {p.pointsRequired} แต้ม)</p>
                        <p className="text-xs text-gray-500">ระยะเวลา: {p.startDate} ถึง {p.endDate}</p>
                      </div>
                      <div className="flex gap-2 items-start">
                        <button onClick={() => { setPromoForm(p); setIsPromoModalOpen(true); }} className="text-rose-500 bg-rose-50 p-2 rounded"><Edit size={16}/></button>
                        <button onClick={() => handleDeletePromo(p.id)} className="text-red-500 bg-red-50 p-2 rounded"><Trash2 size={16}/></button>
                      </div>
                   </div>
                 ))}
               </div>
               <Modal isOpen={isPromoModalOpen} onClose={() => setIsPromoModalOpen(false)} title="ข้อมูลโปรโมชัน">
                  <div className="space-y-3">
                     <input type="text" placeholder="ชื่อโปรโมชัน" className="w-full border p-2 rounded" value={promoForm.name} onChange={e => setPromoForm({...promoForm, name: e.target.value})}/>
                     <div className="flex gap-2">
                        <select className="border p-2 rounded" value={promoForm.type} onChange={e => setPromoForm({...promoForm, type: e.target.value})}><option value="percent">%</option><option value="amount">บาท</option></select>
                        <input type="number" placeholder="มูลค่าส่วนลด" className="flex-1 border p-2 rounded" value={promoForm.value} onChange={e => setPromoForm({...promoForm, value: e.target.value})}/>
                     </div>
                     <input type="number" placeholder="แต้มที่ใช้แลก" className="w-full border p-2 rounded" value={promoForm.pointsRequired} onChange={e => setPromoForm({...promoForm, pointsRequired: e.target.value})}/>
                     <div className="flex gap-2">
                        <div className="flex-1"><label className="text-xs">เริ่ม</label><input type="date" className="w-full border p-2 rounded" value={promoForm.startDate} onChange={e => setPromoForm({...promoForm, startDate: e.target.value})}/></div>
                        <div className="flex-1"><label className="text-xs">สิ้นสุด</label><input type="date" className="w-full border p-2 rounded" value={promoForm.endDate} onChange={e => setPromoForm({...promoForm, endDate: e.target.value})}/></div>
                     </div>
                     <button onClick={handleSavePromo} className="w-full bg-rose-600 text-white py-2 rounded font-bold">บันทึก</button>
                  </div>
               </Modal>
             </div>
           )}

           {ownerActiveTab === 'banners' && (
             <div>
               <h2 className="text-2xl font-bold text-rose-900 mb-4">จัดการประกาศ (Banner)</h2>
               <div className="mb-4">
                  <label className="cursor-pointer bg-rose-600 text-white px-4 py-2 rounded-lg font-bold inline-block hover:bg-rose-700">
                    + อัปโหลดภาพประกาศใหม่
                    <input type="file" className="hidden" onChange={e => handleFileUpload(e, handleAddBanner)} />
                  </label>
               </div>
               <div className="space-y-4">
                  {banners.length > 0 ? banners.map((b, idx) => (
                     <div key={b.id} className="relative group rounded-xl overflow-hidden shadow-md bg-white border">
                        <img src={b.image || b.image_url} className="w-full aspect-video object-cover h-48" alt="banner"/>
                        <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">ลำดับที่ {idx + 1}</div>
                        <div className="absolute top-2 right-2 flex gap-2">
                           <button onClick={() => reorderBanners('up', idx)} disabled={idx===0} className="p-2 bg-gray-100 rounded disabled:opacity-30"><ChevronUp/></button>
                           <button onClick={() => reorderBanners('down', idx)} disabled={idx===banners.length-1} className="p-2 bg-gray-100 rounded disabled:opacity-30"><ChevronDown/></button>
                           <button onClick={() => handleDeleteBanner(b.id)} className="bg-red-500 text-white p-1 rounded-full shadow"><Trash2 size={16}/></button>
                        </div>
                     </div>
                  )) : <p>ยังไม่มีประกาศ</p>}
               </div>
             </div>
           )}

           {ownerActiveTab === 'settings' && (
             <div className="space-y-6">
               <h2 className="text-2xl font-bold text-rose-900">ตั้งค่าร้านค้า</h2>
               <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
                  <div className="flex items-center gap-4">
                     <img src={storeInfo.logo} className="w-20 h-20 rounded-full border shadow-sm object-cover" alt="logo"/>
                     <label className="cursor-pointer bg-gray-100 px-3 py-1 rounded text-sm hover:bg-gray-200">
                        เปลี่ยนโลโก้ <input type="file" className="hidden" onChange={e => handleFileUpload(e, url => setStoreInfo({...storeInfo, logo: url}))}/>
                     </label>
                  </div>
                  <input type="text" className="w-full border p-2 rounded" value={storeInfo.name} onChange={e => setStoreInfo({...storeInfo, name: e.target.value})}/>
                  <textarea className="w-full border p-2 rounded h-24" value={storeInfo.description} onChange={e => setStoreInfo({...storeInfo, description: e.target.value})}/>
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl h-48 bg-gray-50">
                       {storeInfo.paymentQr ? <img src={storeInfo.paymentQr} className="h-full object-contain" alt="qr"/> : <QrCode size={48} className="text-gray-300"/>}
                  </div>
                  <label className="block mt-4 text-center cursor-pointer bg-rose-100 text-rose-700 py-2 rounded-lg font-bold hover:bg-rose-200">
                       อัปโหลดภาพ QR Code
                       <input type="file" className="hidden" onChange={e => handleFileUpload(e, (url) => setStoreInfo({...storeInfo, paymentQr: url}))}/>
                  </label>
                  <MockMapSelector selectedLocation={storeInfo.location} onLocationSelect={(loc) => setStoreInfo({...storeInfo, location: loc})} />
                  <button onClick={handleSaveSettings} className="w-full bg-rose-600 text-white py-3 rounded-xl font-bold shadow-lg mt-4">บันทึกการตั้งค่า</button>
               </div>
             </div>
           )}
        </div>
        
        {/* Slip Popup */}
        <Modal isOpen={!!slipModalImage} onClose={() => setSlipModalImage(null)} title="หลักฐานการโอนเงิน">
           <img src={slipModalImage} className="w-full h-auto rounded" alt="slip"/>
        </Modal>
      </div>
    );
  }

  // --- CHEF ---
  if (currentUser.role === 'chef') {
     return (
        <div className="min-h-screen bg-gray-100 p-4">
           <div className="bg-white p-4 rounded-xl shadow-sm mb-4 flex justify-between items-center">
              <h1 className="text-xl font-bold flex items-center gap-2"><ChefHat/> ครัว</h1>
              <div className="flex gap-2">
                 <button onClick={() => setChefActiveTab('active')} className={`px-3 py-1 rounded ${chefActiveTab==='active'?'bg-orange-100 text-orange-600 font-bold':'bg-gray-100'}`}>รายการทำ</button>
                 <button onClick={() => setChefActiveTab('history')} className={`px-3 py-1 rounded ${chefActiveTab==='history'?'bg-orange-100 text-orange-600 font-bold':'bg-gray-100'}`}>ประวัติ</button>
                 <button onClick={handleLogout} className="text-red-500 text-sm font-bold ml-2">ออก</button>
              </div>
           </div>
           
           {chefActiveTab === 'active' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                 {orders.filter(o => o.status === 'cooking').map(order => (
                    <div key={order.order_id || order.id} className="bg-white p-6 rounded-xl shadow-lg border-l-8 border-orange-400">
                       <div className="flex justify-between mb-4">
                          <span className="text-2xl font-bold">#{order.order_id || order.id}</span>
                          <Timer startTime={order.cooking_start_time || order.updated_at} />
                       </div>
                       <div className="space-y-3 mb-6">
                          {order.items.map((item, i) => (
                             <div key={i} className="border-b pb-2">
                                <span className="text-xl font-bold">{item.quantity}x {item.name}</span>
                                {item.note && <div className="text-red-600 font-bold bg-red-100 px-2 rounded inline-block ml-2">{item.note}</div>}
                             </div>
                          ))}
                       </div>
                       <button onClick={() => postData('update_status', {orderId: order.order_id || order.id, status: 'ready_to_ship'})} className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-xl">เสร็จแล้ว (พร้อมส่ง)</button>
                    </div>
                 ))}
              </div>
           )}

           {chefActiveTab === 'history' && (
              <div className="bg-white rounded-xl shadow p-4">
                 <h2 className="font-bold mb-4">ประวัติการทำอาหาร</h2>
                 <div className="divide-y">
                    {orders.filter(o => ['ready_to_ship', 'delivering', 'completed'].includes(o.status)).map(order => (
                       <div key={order.order_id || order.id} className="py-2 flex justify-between">
                          <span>{order.order_id || order.id} ({order.items ? order.items.length : 0} items)</span>
                          <span className="text-gray-500 text-sm">{new Date(order.timestamp || order.created_at).toLocaleString()}</span>
                       </div>
                    ))}
                 </div>
              </div>
           )}
        </div>
     )
  }

  // --- RIDER ---
  if (currentUser.role === 'rider') {
     const myStats = getRiderStats(currentUser.user_id || currentUser.id, riderStatsTab);
     return (
        <div className="min-h-screen bg-gray-100 p-4 pb-20">
           <div className="bg-green-600 text-white p-4 rounded-xl shadow-sm mb-4 flex justify-between items-center">
              <h1 className="font-bold">Rider: {currentUser.full_name}</h1>
              <div className="flex gap-2">
                 <button onClick={() => setRiderActiveTab('active')} className="bg-white/20 px-2 py-1 rounded">งาน</button>
                 <button onClick={() => setRiderActiveTab('stats')} className="bg-white/20 px-2 py-1 rounded">สถิติ</button>
                 <button onClick={handleLogout} className="bg-white/20 px-2 py-1 rounded">ออก</button>
              </div>
           </div>

           {riderActiveTab === 'active' ? (
              <div className="space-y-4">
                 {/* Job Board */}
                 <h2 className="font-bold text-gray-700">งานใหม่</h2>
                 {orders.filter(o => o.status === 'ready_to_ship' && o.order_type === 'delivery').map(job => (
                    <div key={job.order_id || job.id} className="bg-white p-4 rounded-xl shadow-sm border border-green-200">
                       <div className="flex justify-between font-bold mb-2"><span>#{job.order_id || job.id}</span><span className="text-green-600">{formatCurrency(job.delivery_fee)}</span></div>
                       <p className="text-sm text-gray-500 mb-2"><MapPin size={14} className="inline"/> {job.delivery_distance} km</p>
                       <button onClick={() => postData('update_status', {orderId: job.order_id || job.id, status: 'delivering', riderId: currentUser.user_id})} className="w-full bg-gray-900 text-white py-2 rounded-lg font-bold">รับงาน</button>
                    </div>
                 ))}
                 
                 {/* Active Job */}
                 <h2 className="font-bold text-gray-700 mt-6">งานที่กำลังส่ง</h2>
                 {orders.filter(o => o.status === 'delivering' && o.rider_id == (currentUser.user_id || currentUser.id)).map(job => (
                    <div key={job.order_id || job.id} className="bg-white p-4 rounded-xl shadow-lg border-2 border-yellow-400">
                       <p className="font-bold text-lg text-yellow-700 mb-2">กำลังส่ง #{job.order_id || job.id}</p>
                       <p className="text-xs text-gray-500 mb-2">เวลาสั่ง: {new Date(job.timestamp || job.created_at).toLocaleString()}</p>
                       <div className="bg-gray-50 p-3 rounded mb-3">
                          <p className="font-bold mb-1">{job.customerName}</p>
                          <a href={`tel:${job.customerPhone}`} className="text-blue-600 block mb-2"><Phone size={14} className="inline"/> {job.customerPhone}</a>
                          {job.location && (
                              <div className="h-32 bg-gray-200 rounded relative overflow-hidden mb-2">
                                 <MockMapSelector selectedLocation={job.location} readOnly={true} />
                              </div>
                          )}
                          <div className="text-sm border-t pt-2">
                             {job.items && job.items.map((it, i) => <div key={i}>{it.quantity}x {it.name}</div>)}
                          </div>
                       </div>
                       <a href={`https://www.google.com/maps/dir/?api=1&destination=${job.location?.lat},${job.location?.lng}`} target="_blank" className="block text-center bg-blue-100 text-blue-700 py-2 rounded mb-2 font-bold">นำทาง</a>
                       <button onClick={() => postData('update_status', {orderId: job.order_id || job.id, status: 'completed'})} className="w-full bg-green-600 text-white py-3 rounded-lg font-bold">ส่งสำเร็จ</button>
                    </div>
                 ))}
              </div>
           ) : (
              <div className="bg-white rounded-xl shadow p-4">
                 <div className="flex justify-center gap-2 mb-4">
                    {['day','week','month'].map(t => <button key={t} onClick={() => setRiderStatsTab(t)} className={`px-3 py-1 rounded uppercase ${riderStatsTab===t?'bg-green-100 text-green-700 font-bold':'bg-gray-100'}`}>{t}</button>)}
                 </div>
                 <div className="text-center p-4 bg-green-50 rounded-xl mb-4">
                    <p className="text-gray-500">รายได้รวม</p>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(myStats.income)}</p>
                    <p className="text-sm text-gray-400">{myStats.count} งาน</p>
                 </div>
                 {/* Detailed List */}
                 <div className="space-y-3">
                    {myStats.orders.map((o, idx) => (
                       <div key={idx} className="flex justify-between items-center border-b pb-2 text-sm">
                          <div>
                             <span className="font-bold block">#{o.order_id || o.id}</span>
                             <span className="text-xs text-gray-500">{new Date(o.timestamp || o.created_at).toLocaleString()}</span>
                          </div>
                          <div className="text-right">
                             <span className="block font-bold text-green-600">+{formatCurrency(o.delivery_fee)}</span>
                             <span className="text-xs text-gray-400">{o.customerName}</span>
                          </div>
                       </div>
                    ))}
                 </div>
              </div>
           )}
        </div>
     )
  }

  // --- CUSTOMER ---
  if (currentUser.role === 'customer') {
     const foodTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
     const distance = deliveryType === 'delivery' ? calculateDistance(storeInfo.location.lat, storeInfo.location.lng, customerLocation?.lat, customerLocation?.lng) : 0;
     const deliveryFee = deliveryType === 'delivery' ? (distance <= deliveryConfig.baseDistance ? deliveryConfig.basePrice : deliveryConfig.basePrice + (distance - deliveryConfig.baseDistance) * deliveryConfig.pricePerKm) : 0;
     let discount = 0;
     if (selectedPromo) discount = selectedPromo.type === 'percent' ? (foodTotal * selectedPromo.value / 100) : selectedPromo.value;
     const grandTotal = Math.max(0, foodTotal - discount) + deliveryFee;

     return (
        <div className="min-h-screen bg-rose-50 pb-20 font-sans">
           <div className="max-w-md mx-auto bg-white min-h-screen shadow-xl">
             <div className="bg-white p-4 shadow-sm flex justify-between items-center sticky top-0 z-50">
                <div className="flex items-center gap-2"><img src={storeInfo.logo} className="w-8 h-8 rounded-full"/><span className="font-bold text-rose-800">{storeInfo.name}</span></div>
                <div className="flex items-center gap-3"><span className="text-xs font-bold text-orange-500"><Star size={12} className="inline"/> {currentUser.points || 0} แต้ม</span><button onClick={handleLogout}><LogOut size={18} className="text-gray-400"/></button></div>
             </div>
             
             <div className="flex bg-white shadow-sm mb-4 text-sm sticky top-16 z-40">
               <button onClick={() => setActiveTab('menu')} className={`flex-1 py-3 ${activeTab==='menu'?'border-b-2 border-rose-500 text-rose-600 font-bold':'text-gray-500'}`}>เมนู</button>
               <button onClick={() => setActiveTab('cart')} className={`flex-1 py-3 ${activeTab==='cart'?'border-b-2 border-rose-500 text-rose-600 font-bold':'text-gray-500'}`}>ตะกร้า ({cart.length})</button>
               <button onClick={() => setActiveTab('history')} className={`flex-1 py-3 ${activeTab==='history'?'border-b-2 border-rose-500 text-rose-600 font-bold':'text-gray-500'}`}>ประวัติ</button>
             </div>

             <div className="p-4">
                {activeTab === 'menu' && (
                   <div className="grid gap-4">
                      {/* Banner removed */}
                      {menuItems.filter(i => i.is_available).map(item => (
                         <div key={item.menu_id || item.id} className="bg-white p-3 rounded-2xl shadow-sm flex gap-3 border border-rose-50 relative">
                           {(item.is_best_seller || item.isBestSeller) && <span className="absolute top-0 left-0 bg-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-br-lg rounded-tl-lg shadow z-10">Best Seller</span>}
                           <img src={item.image_url || item.image} className="w-24 h-24 object-cover rounded-xl bg-gray-100" alt={item.name}/>
                           <div className="flex-1 flex flex-col justify-between py-1">
                             <div><h3 className="font-bold text-gray-800 text-lg">{item.name}</h3><p className="text-gray-500 text-xs">{item.description}</p></div>
                             <div className="flex justify-between items-end"><p className="text-rose-600 font-bold text-lg">{item.price}.-</p><button onClick={() => addToCart(item)} className="bg-rose-100 text-rose-600 p-2 rounded-full hover:bg-rose-200"><Plus size={18}/></button></div>
                           </div>
                         </div>
                      ))}
                   </div>
                )}

                {activeTab === 'cart' && (
                   <div className="space-y-4">
                     {cart.length === 0 ? <p className="text-center text-gray-400 mt-10">ตะกร้าว่างเปล่า</p> : (
                       <>
                         {cart.map((item, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-xl shadow-sm space-y-2">
                               <div className="flex justify-between items-center">
                                  <div><p className="font-bold">{item.name}</p><p className="text-xs text-gray-500">{item.price}.-</p></div>
                                  <div className="flex items-center gap-2"><button onClick={() => item.quantity > 1 ? updateCartItem(item.id, -1) : removeFromCart(item.id)} className="bg-gray-200 w-6 h-6 rounded-full flex items-center justify-center">-</button><span>{item.quantity}</span><button onClick={() => updateCartItem(item.id, 1)} className="bg-rose-100 w-6 h-6 rounded-full flex items-center justify-center text-rose-600">+</button></div>
                               </div>
                               <input type="text" placeholder="ระบุความต้องการเพิ่มเติม (เช่น ไม่เผ็ด)" className="w-full text-xs border p-1 rounded bg-gray-50" value={item.note} onChange={(e) => updateCartItem(item.id, 0, e.target.value)}/>
                            </div>
                         ))}
                         <div className="bg-white p-4 rounded-xl shadow-sm">
                            <h3 className="font-bold mb-3">ข้อมูลการสั่งซื้อ</h3>
                            <div className="mb-3">
                               <label className="text-xs font-bold text-gray-600">ความต้องการเพิ่มเติมสำหรับออร์เดอร์นี้</label>
                               <textarea className="w-full border p-2 rounded text-sm h-16 bg-gray-50" placeholder="เช่น ขอช้อนส้อม, แยกน้ำ" value={orderNote} onChange={(e) => setOrderNote(e.target.value)}></textarea>
                            </div>
                            <div className="flex gap-2 mb-3">
                               <button onClick={() => setDeliveryType('delivery')} className={`flex-1 py-2 rounded border ${deliveryType==='delivery'?'bg-rose-50 border-rose-500 text-rose-700':'bg-gray-50'}`}>ไรเดอร์ส่ง</button>
                               <button onClick={() => setDeliveryType('pickup')} className={`flex-1 py-2 rounded border ${deliveryType==='pickup'?'bg-rose-50 border-rose-500 text-rose-700':'bg-gray-50'}`}>รับเอง</button>
                            </div>
                            {deliveryType === 'delivery' && (
                               <div className="mb-3">
                                  <p className="text-xs text-rose-600 mb-1 font-bold">* ปักหมุดเพื่อคำนวณค่าส่ง</p>
                                  <MockMapSelector selectedLocation={customerLocation} onLocationSelect={setCustomerLocation} storeLocation={storeInfo.location} />
                               </div>
                            )}
                            <select className="w-full border p-2 rounded text-sm mb-3" onChange={(e) => setSelectedPromo(promotions.find(p => p.id === parseInt(e.target.value)) || null)} value={selectedPromo?.id || ''}>
                               <option value="">ใช้แต้มแลกส่วนลด...</option>
                               {promotions.map(p => <option key={p.id} value={p.id} disabled={currentUser.points < p.pointsRequired}>{p.name} (ใช้ {p.pointsRequired} แต้ม)</option>)}
                            </select>
                            <div className="space-y-1 text-sm border-t pt-2">
                               <div className="flex justify-between"><span>ค่าอาหาร</span><span>{formatCurrency(foodTotal)}</span></div>
                               {deliveryType === 'delivery' && <div className="flex justify-between"><span>ค่าส่ง</span><span>{formatCurrency(deliveryFee)}</span></div>}
                               {selectedPromo && <div className="flex justify-between text-green-600"><span>ส่วนลด</span><span>-{formatCurrency(discount)}</span></div>}
                               <div className="flex justify-between font-bold text-lg text-rose-600 mt-2"><span>รวมสุทธิ</span><span>{formatCurrency(grandTotal)}</span></div>
                            </div>
                         </div>
                         <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                            <p className="text-xs text-gray-500 mb-2">สแกนจ่ายเงินและแนบสลิป</p>
                            {storeInfo.paymentQr && <img src={storeInfo.paymentQr} className="w-32 h-32 object-contain mx-auto mb-2 border rounded" alt="qr"/>}
                            <label className="cursor-pointer bg-gray-100 block py-2 rounded hover:bg-gray-200">
                               {slipImageFile ? <span className="text-green-600 flex items-center justify-center gap-1"><CheckCircle size={14}/> แนบแล้ว</span> : <span className="flex items-center justify-center gap-1"><Upload size={14}/> เลือกรูปสลิป</span>}
                               <input type="file" className="hidden" onChange={e => handleFileUpload(e, setSlipImageFile)}/>
                            </label>
                         </div>
                         <button onClick={placeOrder} className="w-full bg-rose-600 text-white py-3 rounded-xl font-bold shadow-lg">ยืนยันการสั่งซื้อ</button>
                       </>
                     )}
                   </div>
                )}

                {activeTab === 'history' && (
                   <div className="space-y-4">
                      {orders.filter(o => o.customer_id == (currentUser.user_id || currentUser.id)).sort((a,b) => new Date(b.created_at) - new Date(a.created_at)).map(order => (
                         <div key={order.order_id || order.id} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-rose-400 relative">
                            <div className="flex justify-between mb-1">
                               <span className="font-bold text-rose-800">{order.order_id || order.id}</span>
                               <span className={`text-xs px-2 py-0.5 rounded ${order.status==='waiting_payment'?'bg-red-100 text-red-700':order.status==='completed'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>
                                 {order.status === 'waiting_payment' ? 'รอร้านยืนยัน' : order.status === 'cancelled' ? 'ยกเลิก' : order.status === 'completed' ? 'สำเร็จ' : 'กำลังดำเนินการ'}
                               </span>
                            </div>
                            <p className="text-sm text-gray-500">{order.items ? order.items.length : 0} รายการ | {formatCurrency(order.grand_total || order.total)}</p>
                            
                            {order.status === 'cancelled' && order.cancelReason && (
                               <div className="bg-red-50 text-red-600 text-xs p-2 rounded mt-2 flex items-start gap-1">
                                  <AlertCircle size={12} className="mt-0.5"/>
                                  <span>ร้านแจ้งยกเลิก: "{order.cancelReason}"</span>
                               </div>
                            )}

                            <div className="mt-3 flex gap-2 justify-end">
                               {order.status === 'waiting_payment' && (
                                  <button onClick={() => cancelOrder(order.order_id || order.id, 'ลูกค้ากดยกเลิก')} className="text-xs bg-red-100 text-red-600 px-3 py-1 rounded">ยกเลิกออร์เดอร์</button>
                               )}
                               {order.status === 'ready_to_ship' && order.order_type === 'pickup' && (
                                  <button onClick={() => updateStatus(order.order_id || order.id, 'completed')} className="text-xs bg-green-600 text-white px-3 py-1 rounded font-bold">ฉันได้รับสินค้าแล้ว</button>
                               )}
                               {order.status === 'completed' && !order.review && (
                                  <button onClick={() => { setReviewForm({ ...reviewForm, orderId: order.order_id || order.id }); setIsReviewModalOpen(true); }} className="text-xs bg-yellow-400 text-white px-3 py-1 rounded font-bold shadow">ให้คะแนน/รีวิว</button>
                               )}
                            </div>
                         </div>
                      ))}
                      <Modal isOpen={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)} title="ให้คะแนนความพึงพอใจ">
                         <div className="space-y-4 text-center">
                            <div className="flex justify-center gap-2">
                               {[1,2,3,4,5].map(star => (
                                  <button key={star} onClick={() => setReviewForm({...reviewForm, rating: star})} className={`p-1 ${reviewForm.rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}><Star size={32} fill="currentColor"/></button>
                               ))}
                            </div>
                            <textarea className="w-full border p-3 rounded-xl bg-gray-50" placeholder="เขียนความประทับใจ..." value={reviewForm.comment} onChange={e => setReviewForm({...reviewForm, comment: e.target.value})}></textarea>
                            <button onClick={submitReview} className="w-full bg-rose-600 text-white py-2 rounded-xl font-bold">ส่งรีวิว</button>
                         </div>
                      </Modal>
                   </div>
                )}
             </div>
           </div>
        </div>
     )
  }

  return null;
}